import { Restrictions } from '@repo-level/core-front';
import { TOptions } from 'i18next';
import { CountryCode, isValidPhoneNumber } from 'libphonenumber-js';
import moment from 'moment';
import { Maybe } from 'monet';
import { always, cond, pipe, replace, T } from 'ramda';

export type ValidatorMessage = string | [string, TOptions];

export type Validator<T = any, VS = any> = (
  value: T | undefined,
  values: VS,
  length?: number
) => ValidatorMessage | void;

export function composeValidators<T>(
  ...validators: Validator<T>[]
): Validator<T> {
  return (...args) => {
    for (let validator of validators) {
      const error = validator(...args);

      if (error != null) return error;
    }
  };
}

export const requiredFactory: (msg: ValidatorMessage) => Validator<any> = (
  msg
) => (value) => (value == null ? msg : undefined);

export const required = requiredFactory('validation:required');

export const requiredDefault = requiredFactory('validation:requiredDefault');

export const requiredAndNotZeroFactory: (
  msg: ValidatorMessage
) => Validator<any> = (msg) => (value) => {
  return !value || value === '0' ? msg : undefined;
};

export const requiredAndNotZero = requiredAndNotZeroFactory(
  'validation:required'
);

export function regex<T extends string>(
  re: RegExp,
  msg: ValidatorMessage
): Validator<T> {
  return (value) =>
    value == null ? undefined : re.test(value) ? undefined : msg;
}

export function lt<T extends string>(
  param: number,
  msg: ValidatorMessage
): Validator<T> {
  return (value) =>
    value == null ? undefined : value.length < param ? undefined : msg;
}

export const lt256 = lt(256, ['validation:string.max', { val: 255 }]);

export function gt<T extends string>(
  param: number,
  msg: ValidatorMessage
): Validator<T> {
  return (value) =>
    value == null ? undefined : value.length > param ? undefined : msg;
}

export const latinLettersRefCodeRe = /^[a-zA-Z0-9_]+$/;

export const maskRefCode = (value: string) => {
  return value.slice(0, 20).replace(/[^a-zA-Z0-9_]/g, '');
};

const refCodeRe = /^[A-Za-z0-9_]{3,20}$/;
export const refCodeRegexValidator = regex(
  refCodeRe,
  'validation:referralCode'
);
export const refCodeValidator = composeValidators(
  required,
  refCodeRegexValidator
);

// eslint-disable-next-line
const passRe = /^.{8,30}$/;
export const passRegexValidator = regex(passRe, ' ');
export const passValidator = composeValidators(required, passRegexValidator);

// eslint-disable-next-line
export const emailRe = /^((([a-z]|\d|[!#\$%&'\*\+\-\/=\?\^_`{\|}~]|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])+(\.([a-z]|\d|[!#\$%&'\*\+\-\/=\?\^_`{\|}~]|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])+)*)|((\x22)((((\x20|\x09)*(\x0d\x0a))?(\x20|\x09)+)?(([\x01-\x08\x0b\x0c\x0e-\x1f\x7f]|\x21|[\x23-\x5b]|[\x5d-\x7e]|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])|(\\([\x01-\x09\x0b\x0c\x0d-\x7f]|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF]))))*(((\x20|\x09)*(\x0d\x0a))?(\x20|\x09)+)?(\x22)))@((([a-z]|\d|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])|(([a-z]|\d|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])([a-z]|\d|-|\.|_|~|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])*([a-z]|\d|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])))\.)+(([a-z]|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])|(([a-z]|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])([a-z]|\d|-|\.|_|~|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])*([a-z]|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])))$/i;
export const email = regex(emailRe, 'validation:email');

export const checkboxRequired: Validator<boolean> = (value) =>
  !value ? 'validation:required' : undefined;

// eslint-disable-next-line
export const dateRe = /^((0[1-9]|[12][0-9]|3[01])(\/)(0[13578]|1[02]))|((0[1-9]|[12][0-9])(\/)(02))|((0[1-9]|[12][0-9]|3[0])(\/)(0[469]|11))(\/)\d{4}$/;
export const dateRegexValidator = regex(dateRe, 'validation:invalid');

export const zipCodeAURe = /^(0[289][0-9]{2})|([1-9][0-9]{3})$/;
export const zipCodeAURegexValidator = regex(zipCodeAURe, 'validation:invalid');

export const adultValidator: Validator<string> = (dob) => {
  if (dob == null) return undefined;

  const [day, month, year] = dob.split('/');

  const myDob = new Date(`${month}/${day}/${year}`);
  const today = new Date();
  const maxDob = new Date(
    today.getFullYear() - 18,
    today.getMonth(),
    today.getDate()
  );

  return maxDob.getTime() > myDob.getTime() ? undefined : 'validation:age18';
};

export const numberPhoneValidator: Validator<string> = (
  value,
  country?: string
) => {
  return value != null &&
    value.length !== 0 &&
    (country == null
      ? isValidPhoneNumber(value.toString())
      : isValidPhoneNumber(value.toString(), country as CountryCode))
    ? undefined
    : 'validation:invalid';
};

export const maskAmount = (value: string) => {
  const strValue = String(value || '');

  if (strValue === '.') return '0.';

  const sanitizedValue = strValue.replace(/[^\d.]/g, '');
  const [wholePart, decimalPart] = sanitizedValue.split('.');

  if (Number(wholePart) >= 1000) return wholePart;
  if (!decimalPart) return sanitizedValue;

  return `${wholePart}.${decimalPart.replace(/\D/g, '')}`;
};

const cardNumberRe = /^[0-9]{4}\s?[0-9]{4}\s?[0-9]{4}\s?[0-9]{4,6}$/;
export const cardNumber = regex(cardNumberRe, 'validation:invalid');

const cvvCardRe = /^[0-9]{3,4}$/i;
export const cvvCard = regex(cvvCardRe, 'validation:invalidCvv');

const cardExpiryDateRe = /^(0[1-9]|1[0-2])\/?([0-9]{2})$/i;
export const cardExpiryDate = regex(cardExpiryDateRe, 'validation:invalid');

type AmountLimitMsg = (restrictions: Restrictions) => ValidatorMessage;

const amountLimitFactory = (msgMin: AmountLimitMsg, msgMax: AmountLimitMsg) => (
  restrictions: Restrictions
) => {
  const toNumber = (value: any) =>
    Maybe.fromEmpty(value)
      .map(pipe(replace(',', ''), (s) => Number(s)))
      .getOrElse(0);

  const below = (restrictions: Restrictions) => (value: number) =>
    restrictions.minAmountValue != null && restrictions.minAmountValue > value;

  const above = (restrictions: Restrictions) => (value: number) =>
    restrictions.maxAmountValue != null && restrictions.maxAmountValue < value;

  const checkLimits = (restrictions: Restrictions) =>
    cond<number, ValidatorMessage | undefined>([
      [below(restrictions), always(msgMin(restrictions))],
      [above(restrictions), always(msgMax(restrictions))],
      [T, always(undefined)],
    ]);

  return pipe(toNumber, checkLimits(restrictions));
};

const amountLimitMsgMin: AmountLimitMsg = ({
  minAmountValue,
  amountCurrencyCode,
}) => [
  'validation:amount.min',
  { amount: minAmountValue, currency: amountCurrencyCode },
];

const amountLimitMsgMax: AmountLimitMsg = ({
  maxAmountValue,
  amountCurrencyCode,
}) => [
  'validation:amount.max',
  { amount: maxAmountValue, currency: amountCurrencyCode },
];

export const amountLimit = amountLimitFactory(
  amountLimitMsgMin,
  amountLimitMsgMax
);

export const validateEmail = composeValidators(required, email);

export const validatePassword = composeValidators(required, passValidator);

const isLaterThanToday = (date: string) => {
  const inputDate = moment(date, 'MM/YY').startOf('month');
  const today = moment().startOf('day');

  return inputDate.isAfter(today, 'month');
};

export const cardExpiryDateValidator: Validator<string> = (value?: string) => {
  return isLaterThanToday(value!) ? undefined : 'validation:expiryDate.invalid';
};
